import { jsPDF as JsPDF } from 'jspdf';

export interface InvoicePdfData {
  invoiceNumber?: string;
  invoiceDate?: string;
  financialYear?: string;
  referenceNo?: string;
  seller?: {
    name?: string;
    mobile?: string;
    email?: string;
    billingAddress?: string;
    gstin?: string;
    cin?: string;
    sacCode?: string;
  };
  buyer?: {
    name?: string;
    mobile?: string;
    email?: string;
    address?: string;
    billingAddress?: string;
    pan?: string;
    chargingLocation?: string;
  };
  session?: {
    charger?: string;
    connector?: string;
    date?: string;
    time?: string;
    duration?: string;
    energy?: string;
    evVin?: string;
    evNumber?: string;
    bookingId?: string;
    remark?: string;
  };
  costs?: {
    connectorPrice?: number | string;
    consumptionCost?: number | string;
    platformFee?: number | string;
    sessionFee?: number | string;
  };
  totalAmount?: number | string;
  currency?: string;
}

/**
 * Checks if a value is valid and non-empty.
 * @param value Value to check
 * @returns Boolean indicating if value is valid
 */
const isValidValue = (value?: string | number | null): boolean => {
  if (value === undefined || value === null) return false;
  const valueString = String(value).trim();
  return valueString !== '' && valueString !== '-';
};

/**
 * Generates and downloads a Tax Invoice PDF matching the official template.
 * @param data Invoice details data structure
 * @param outputFilename Optional custom filename for the generated PDF
 */
export const generateInvoicePdf = (data: InvoicePdfData, outputFilename?: string): void => {
  const document = new JsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const marginLeft = 20;
  const marginRight = 190;
  const colValueX = 70;
  const maxValueWidth = marginRight - colValueX;
  const pageHeight = 297;
  let currentY = 24;

  /**
   * Helper to ensure content fits on page or starts a new page
   * @param neededHeight Required height for next block
   */
  const checkPageBreak = (neededHeight = 10) => {
    if (currentY + neededHeight > pageHeight - 20) {
      document.addPage();
      currentY = 24;
    }
  };

  /**
   * Renders a bold section header
   * @param title Title of the section
   */
  const renderSectionHeader = (title: string) => {
    checkPageBreak(12);
    currentY += 3;
    document.setFont('helvetica', 'bold');
    document.setFontSize(12);
    document.setTextColor(0, 0, 0);
    document.text(title, marginLeft, currentY);
    currentY += 6;
  };

  /**
   * Renders a single labeled row with bold label and normal value
   * @param label Row label
   * @param value Row value
   */
  const renderRow = (label: string, value?: string | number | null) => {
    if (value === undefined || value === null || value === '' || value === '-') {
      return;
    }
    const valueString = String(value);
    document.setFont('helvetica', 'normal');
    document.setFontSize(10);

    const splitValues = document.splitTextToSize(valueString, maxValueWidth);
    const rowHeight = Math.max(5.5, splitValues.length * 4.8);

    checkPageBreak(rowHeight + 2);

    // Label column in bold
    document.setFont('helvetica', 'bold');
    document.setFontSize(10);
    document.setTextColor(0, 0, 0);
    document.text(label, marginLeft, currentY);

    // Value column in regular
    document.setFont('helvetica', 'normal');
    document.setFontSize(10);
    document.setTextColor(0, 0, 0);
    document.text(splitValues, colValueX, currentY);

    currentY += rowHeight;
  };

  const currency = data.currency || 'INR';

  // Section 1: Invoice
  renderSectionHeader('Invoice');
  renderRow('Invoice No', data.invoiceNumber || 'N/A');
  renderRow('Date', data.invoiceDate);
  renderRow('Financial Year', data.financialYear);
  renderRow('Reference No', data.referenceNo);

  // Section 2: Seller
  if (data.seller && Object.values(data.seller).some(isValidValue)) {
    renderSectionHeader('Seller');
    renderRow('Name', data.seller.name);
    renderRow('Mobile', data.seller.mobile);
    renderRow('Email', data.seller.email);
    renderRow('Billing Address', data.seller.billingAddress);
    renderRow('GSTIN', data.seller.gstin);
    renderRow('CIN', data.seller.cin);
    renderRow('SAC Code', data.seller.sacCode);
  }

  // Section 3: Buyer
  if (data.buyer && Object.values(data.buyer).some(isValidValue)) {
    renderSectionHeader('Buyer');
    renderRow('Name', data.buyer.name);
    renderRow('Mobile', data.buyer.mobile);
    renderRow('Email', data.buyer.email);
    renderRow('Address', data.buyer.address || data.buyer.billingAddress);
    renderRow('PAN', data.buyer.pan);
    renderRow('Charging Location', data.buyer.chargingLocation);
  }

  // Section 4: Charging session
  if (data.session && Object.values(data.session).some(isValidValue)) {
    renderSectionHeader('Charging session');
    renderRow('Charger', data.session.charger);
    renderRow('Connector', data.session.connector);
    renderRow('Date', data.session.date);
    renderRow('Time', data.session.time);
    renderRow('Duration', data.session.duration);
    renderRow('Energy', data.session.energy);
    renderRow('Vehicle VIN', data.session.evVin);
    renderRow('Vehicle No', data.session.evNumber);
    renderRow('Booking ID', data.session.bookingId);
    renderRow('Remark', data.session.remark);
  }

  // Section 5: Cost breakdown
  if (data.costs && Object.values(data.costs).some(isValidValue)) {
    renderSectionHeader('Cost breakdown');
    if (isValidValue(data.costs.connectorPrice)) {
      renderRow('Connector Price', `${data.costs.connectorPrice} ${currency}`);
    }
    if (isValidValue(data.costs.consumptionCost)) {
      renderRow('Consumption Cost', `${data.costs.consumptionCost} ${currency}`);
    }
    if (isValidValue(data.costs.platformFee)) {
      renderRow('Platform Fee', `${data.costs.platformFee} ${currency}`);
    }
    if (isValidValue(data.costs.sessionFee)) {
      renderRow('Surcharge Fee', `${data.costs.sessionFee} ${currency}`);
    }
  }

  // Horizontal divider
  currentY += 2;
  checkPageBreak(15);
  document.setDrawColor(0, 0, 0);
  document.setLineWidth(0.6);
  document.line(marginLeft, currentY, marginRight, currentY);

  // Total
  currentY += 6;
  document.setFont('helvetica', 'bold');
  document.setFontSize(12);
  document.setTextColor(0, 0, 0);
  document.text('Total', marginLeft, currentY);

  const totalDisplay = `${data.totalAmount ?? '--'} ${currency}`;
  document.text(totalDisplay, marginRight, currentY, { align: 'right' });

  // Save PDF
  const fallbackName = data.invoiceNumber
    ? data.invoiceNumber.replace(/[/\\:*?"<>|]/g, '_')
    : 'invoice';
  const finalFilename = outputFilename || `${fallbackName}.pdf`;

  document.save(finalFilename);
};

export default generateInvoicePdf;
